import MarkdownContent from '@/components/MarkdownContent';

const MAX_PREVIEW_ROWS = 20;

function TablePreview({ rows }) {
  if (!Array.isArray(rows) || !rows.length) return null;
  const columns = Object.keys(rows[0]);
  const shown = rows.slice(0, MAX_PREVIEW_ROWS);
  return (
    <div className="overflow-x-auto rounded-md border border-[var(--app-border)]">
      <table className="border-collapse text-sm w-full">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} className="border border-[var(--app-border)] px-2 py-1 text-left bg-[var(--app-border)]/20">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => <td key={c} className="border border-[var(--app-border)] px-2 py-1">{String(row[c] ?? '')}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > MAX_PREVIEW_ROWS && (
        <p className="text-xs text-secondary-500 px-2 py-1">Showing {MAX_PREVIEW_ROWS} of {rows.length} rows — download for the full file.</p>
      )}
    </div>
  );
}

const MAX_PREVIEW_TEXT_CHARS = 4000;

function TextPreview({ content }) {
  const shown = (content || '').slice(0, MAX_PREVIEW_TEXT_CHARS);
  return (
    <div className="rounded-md border border-[var(--app-border)] p-3">
      <pre className="text-sm whitespace-pre-wrap break-words">{shown}</pre>
      {content && content.length > MAX_PREVIEW_TEXT_CHARS && (
        <p className="text-xs text-secondary-500 mt-1">Truncated — download for the full file.</p>
      )}
    </div>
  );
}

function SlidesPreview({ title, slides }) {
  return (
    <div className="space-y-2">
      {title && <div className="rounded-md border border-[var(--app-border)] p-3 font-semibold text-center">{title}</div>}
      {(slides || []).map((slide, i) => (
        <div key={i} className="rounded-md border border-[var(--app-border)] p-3">
          <p className="font-medium mb-1">{slide.heading}</p>
          {slide.bullets?.length > 0 && (
            <ul className="list-disc pl-5 text-sm">
              {slide.bullets.map((b, j) => <li key={j}>{b}</li>)}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Renders a generated file's content inline in the chat, not just a download chip —
 * services/files/fileStorage.js's own comment documents the `preview` shape each
 * generate_* tool attaches. `downloadUrl` is this app's own authenticated endpoint
 * (/api/agent/files/:id or the embed equivalent), which redirects to the real storage
 * URL — used directly as the PDF iframe's src since browsers follow that redirect
 * transparently, and it keeps the ownership check in front of the asset either way.
 */
export default function FilePreview({ preview, downloadUrl }) {
  if (!preview) return null;
  if (preview.type === 'pdf') {
    return <iframe src={downloadUrl} title="PDF preview" className="w-full h-80 rounded-md border border-[var(--app-border)]" />;
  }
  if (preview.type === 'image') {
    return <img src={downloadUrl} alt="Attachment preview" className="max-w-full max-h-80 rounded-md border border-[var(--app-border)]" />;
  }
  if (preview.type === 'table') return <TablePreview rows={preview.rows} />;
  if (preview.type === 'json') return <TextPreview content={JSON.stringify(preview.data, null, 2)} />;
  if (preview.type === 'text') return <TextPreview content={preview.content} />;
  if (preview.type === 'markdown') {
    return (
      <div className="rounded-md border border-[var(--app-border)] p-3">
        <MarkdownContent content={preview.content} />
      </div>
    );
  }
  if (preview.type === 'slides') return <SlidesPreview title={preview.title} slides={preview.slides} />;
  return null;
}
